import { LOCALE_DIRECTION } from '@aysar/contracts';

import {
  type PreferencesState,
  type ResolvedTheme,
  type ThemePreference,
} from '@/shared/store/reducers/preferences/preferences.types';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Resolves `system` against the operating system; an explicit choice wins.
 *
 * `matchMedia` is wrapped because it is absent in some environments and throws
 * in others, and a colour preference is never worth a white screen.
 */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;

  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Dark mode is class-based (`darkMode: 'class'` in the Sanctuary preset). */
export function applyThemeToDocument(theme: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

/**
 * Writes theme, language and direction onto `<html>` and returns the resolved
 * theme.
 *
 * Called from `main.tsx` BEFORE `createRoot().render()`, which is the whole
 * point: `dir`, `lang` and `.dark` are on the element before React's first
 * paint, so an Arabic operator in dark mode never sees a flash of a light
 * left-to-right layout. Doing it in an effect instead guarantees that flash on
 * every single page load.
 *
 * It is TypeScript rather than an inline `<script>` in `index.html` for the
 * ordinary reasons: a script tag is untestable, unlintable, and would need its
 * own copy of the storage key and the direction table.
 *
 * Direction comes from the locale and nothing else (U7/M6's web equivalent). No
 * component ever branches on it — they use logical properties and let `dir` do
 * the mirroring.
 */
export function applyPreferencesToDocument(preferences: PreferencesState): ResolvedTheme {
  const theme = resolveTheme(preferences.theme);

  applyThemeToDocument(theme);
  document.documentElement.lang = preferences.locale;
  document.documentElement.dir = LOCALE_DIRECTION[preferences.locale];

  return theme;
}
